import { Injectable, InjectionToken, inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/browser';
import { OAuthService } from '../../external/angular-oauth2-oidc-jwks/public_api';
import { WVCommunicationService } from '../wvcommunication.service';
import { AuthService } from './auth.service';

/** Código gRPC `NotFound`: `GetMe` lo devuelve cuando la persona existe en Identity pero no en `user`. */
const GRPC_STATUS_NOT_FOUND = 5;

/** A dónde lleva el portal a una cuenta que solo existe en Identity (BET-515). */
export interface BpIncompleteAccountRedirect {
  commands: string[];
  queryParams: Record<string, string>;
}

/**
 * Solo bp-portal lo provee (tiene `/register`). bp-advisor y bp-admin comparten `BpAuthGuard` y
 * `AuthService`, pero no tienen esa ruta: sin este valor el servicio no hace nada y la guarda se
 * comporta como siempre.
 */
export const BP_INCOMPLETE_ACCOUNT_REDIRECT =
  new InjectionToken<BpIncompleteAccountRedirect | null>('BP_INCOMPLETE_ACCOUNT_REDIRECT', {
    providedIn: 'root',
    factory: () => null,
  });

/**
 * Cuenta a medias: la persona inició sesión en Identity, pero nunca terminó el registro y no tiene
 * fila en `user`, así que `GetMe` responde `NotFound` y el portal quedaba en blanco (BET-515). La
 * comparten `LoginComponent` y `BpAuthGuard`: un solo lugar con la regla.
 */
@Injectable({ providedIn: 'root' })
export class BpIncompleteAccountService {
  private readonly redirect = inject(BP_INCOMPLETE_ACCOUNT_REDIRECT);
  private readonly router = inject(Router);
  private readonly oauthService = inject(OAuthService);
  private readonly authService = inject(AuthService);
  private readonly wvCommunicationService = inject(WVCommunicationService);

  /**
   * Si el error de `GetMe` es `NotFound` de una persona real (no «entrar como»), borra la sesión del
   * portal y la lleva al registro con la marca que prende el aviso.
   * @returns `true` si lo atendió (ya navega a `/register`); `false` si el error sigue su curso.
   */
  tryRecover(error: unknown): boolean {
    if (!this.redirect || !this.isNotFound(error) || localStorage.getItem('bp-enter-as-id')) {
      return false;
    }

    // Solo la sesión de este portal: `logOut(true)` quita los tokens sin ir al cierre de sesión de
    // Identity (`revokeTokenAndLogout()` y `logout()` sí navegan), para que el registro siga ahí.
    this.oauthService.stopAutomaticRefresh();
    this.authService.clearLocalSession();
    this.oauthService.logOut(true);
    // La WebView móvil se entera por aquí: no cambia de pantalla, solo arma su limpieza de estado.
    this.wvCommunicationService.isLogin(false);

    // Mensaje fijo: nada de correo ni nombre.
    Sentry.captureMessage(
      'Cuenta solo en Identity: GetMe NotFound, se lleva a /register',
      'warning',
    );

    void this.router.navigate(this.redirect.commands, {
      queryParams: this.redirect.queryParams,
      replaceUrl: true,
    });
    return true;
  }

  private isNotFound(error: unknown): boolean {
    return (error as { statusCode?: number } | null)?.statusCode === GRPC_STATUS_NOT_FOUND;
  }
}
